import { createContext, useContext, useEffect, useState } from 'react';
import { ConfirmAlert, showAlert } from '../../components';
import {
  uploadPaymentReminderFile,
  getPaymentReminderFiles,
  unloadPaymentReminderFile,
  getPaymentReminderFileByFileId,
  paymentReminderSenderEmail,
  getLogsListByBatchIdAndSendStatus,
  paymentReminderSenderEmailId,
  updateEmailTemplate,
  getLogsList,
  sendEmailRecords,
  resendEmails,
  loadEmailFile,
  getPendingMailRecords,
  dowloadLogExcelFile,
  abortEmailRecords,
} from '../../services/paymentReminderService';
import { useLoading } from '@/utils/LoadingUtil';
import { useNavigate } from 'react-router-dom';
import paymentReminderModel from '@/model/paymentReminder/PaymentReminder.model';
import { handleApiError } from '@/lib/handleApiError';

const PaymentReminderContext = createContext({
  uploadFile: () => {},
  response: null,
  setResponse: () => {},
});
export const usePaymentReminder = () => {
  return useContext(PaymentReminderContext);
};

const PaymentReminderContextProvider = ({ children }) => {
  const { paymentReminder, setPaymentReminder, intialState } =
    paymentReminderModel();

  const { isLoading, startLoading, stopLoading } = useLoading();
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [response, setResponse] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('');
  const [singleFileData, setSingleFileData] = useState(null); // New state for single file data

  const nav = useNavigate();

  const [senderEmail, setSenderEmail] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [selectedFrom, setSelectedFrom] = useState(null); // Persist "From"
  const [selectedReminder, setSelectedReminder] = useState(null); // Persist "Reminder"
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedBody, setSelectedBody] = useState('');
  const [logData, setLogData] = useState(null);
  const [selectedBatchIdData, setSelectedBatchIdData] = useState(null);
  const [pendingMailRec, setPendingMailRec] = useState(null);
  const [abortMailList, setAbortMailList] = useState(null);

  useEffect(() => {
    setCurrentPage(0);
    setPageSize(10);
    setSortBy('');
    setSortDir('');
  }, [nav]);

  const uploadFile = async (formData) => {
    try {
      startLoading();
      const data = await uploadPaymentReminderFile(formData);
      if (data.status === 200) {
        showAlert('success', 'File uploaded successfully');
      }
      await getPaymentReminderFilesAPI();
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const getPaymentReminderFilesAPI = async (
    page = currentPage,
    size = pageSize,
    sortField = sortBy,
    sortDirection = sortDir,
  ) => {
    try {
      startLoading();
      const response = await getPaymentReminderFiles(
        page,
        size,
        sortField,
        sortDirection,
      );
      if (response.status === 204) {
        setResponse([]);
        setTotalElements(0);
        return;
      }
      setResponse(response.data._embedded.paymentReminderFileDTOList);
      setTotalElements(response.data.page.totalElements); // Update total elements
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
      setResponse(null);
      setTotalElements(0);
      throw error;
    } finally {
      stopLoading();
    }
  };

  const unloadFile = async (id) => {
    const result = await ConfirmAlert({
      confirmBtnText: 'Delete Payment Reminder File',
    });
    if (!result.isConfirmed) {
      showAlert('error', 'File deletion cancelled');
      stopLoading();
      return;
    }

    try {
      startLoading();
      const response = await unloadPaymentReminderFile(id);
      showAlert('success', 'File unloaded successfully');
      await getPaymentReminderFilesAPI(); // Refresh the file list
    } catch (error) {
      handleApiError(
        'error',
        error.response.data || 'Failed to unload file',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  };

  const getPaymentReminderFileByFileIdAPI = async (id, filterType) => {
    try {
      startLoading();
      const response = await getPaymentReminderFileByFileId(id, filterType);
      if (response.status === 200) {
        setSingleFileData(response.data);

        // Set file and due in paymentReminder
        setPaymentReminder((prev) => ({
          ...prev,
          file: {
            id: response.data.id,
            fileName: response.data.fileName,
            loadDateTime: response.data.loadDateTime,
            rowNos: response.data.rowNos,
            reminderStatus: response.data.reminderStatus,
            startDate: response.data.startDate,
            endDate: response.data.endDate,
            fileLogs: [],
          },
          due: filterType,
        }));
      }
    } catch (error) {
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const sendEmailRecordsAPI = async () => {
    try {
      startLoading();
      const response = await sendEmailRecords(paymentReminder);
      if (response.status === 200) {
        showAlert('success', 'Email sent successfully');
        setPaymentReminder(intialState);
        setSingleFileData(null);
        setSelectedFrom(null);
        setSelectedReminder(null);
        setSelectedSubject('');
        setSelectedBody('');
        setEmailTemplate(null);
        setSenderEmail(null);
        setSelectedBatchIdData(null);
        setLogData(null);
        setCurrentPage(0);
        setPageSize(10);
      }
      nav('/set-reminder');
      await getPaymentReminderFilesAPI(); // Refresh the file list
      showAlert('success', 'Email sent successfully');
    } catch (error) {
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const resetPaymentReminder = () => {
    setPaymentReminder(intialState);
  };

  // Update or add fileLog with latest email/email1 in paymentReminder.file.fileLogs
  const toggleFileLogSelection = (fileLog) => {
    setPaymentReminder((prev) => {
      const exists = prev.file.fileLogs.some((log) => log.id === fileLog.id);
      let newFileLogs;
      if (exists) {
        newFileLogs = prev.file.fileLogs.filter((log) => log.id !== fileLog.id);
      } else {
        // Add the latest fileLog (with updated emails)
        newFileLogs = [
          ...prev.file.fileLogs.filter((log) => log.id !== fileLog.id),
          fileLog,
        ];
      }
      return {
        ...prev,
        file: {
          ...prev.file,
          fileLogs: newFileLogs,
        },
      };
    });
  };

  // Update email/email1 in paymentReminder.file.fileLogs if present
  const updateFileLogEmailInPaymentReminder = (fileLogId, field, value) => {
    setPaymentReminder((prev) => {
      if (!prev.file?.fileLogs?.length) return prev;
      const updatedLogs = prev.file.fileLogs.map((log) =>
        log.id === fileLogId
          ? {
              ...log,
              customer: {
                ...log.customer,
                [field]: value,
              },
            }
          : log,
      );
      return {
        ...prev,
        file: {
          ...prev.file,
          fileLogs: updatedLogs,
        },
      };
    });
  };

  const paymentReminderSenderEmailAPI = async () => {
    try {
      startLoading();
      const response = await paymentReminderSenderEmail();
      if (response.status === 200) {
        setSenderEmail(response.data);
      }
    } catch (error) {
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };
  const paymentReminderSenderEmailAPIId = async (id) => {
    try {
      startLoading();
      const response = await paymentReminderSenderEmailId(id);
      if (response.status === 200) {
        setEmailTemplate(response.data);
      }
    } catch (error) {
      handleApiError('error', error?.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const updateEmailTemplateAPI = async (templateId, data) => {
    if (!data.subject || data.subject.trim() === '') {
      showAlert('error', 'Subject cannot be empty');
      return;
    }

    if (!data.body || data.body.trim() === '') {
      showAlert('error', 'Body cannot be empty');
      return;
    }

    try {
      startLoading();
      const response = await updateEmailTemplate(templateId, data);
      if (response.status === 200) {
        showAlert('success', 'Email template updated successfully');
      }
    } catch (error) {
      handleApiError(
        'error',
        error?.response?.data || 'Failed to update email template',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  };

  const getLogsListAPI = async (page = currentPage, size = pageSize) => {
    try {
      startLoading();
      const response = await getLogsList(page, size);
      if (response.status === 200) {
        setLogData(
          response?.data._embedded
            ?.paymentReminderSendEmailRecordsBatchList || ['No data found'],
        );
        setTotalElements(response.data.page.totalElements); // Update total elements
      } else if (response.status === 204) {
        setLogData(['No data found']);
        setTotalElements(0);
        return;
      }
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
      setResponse(null);
      setTotalElements(0);
      throw error;
    } finally {
      stopLoading();
    }
  };

  const getLogsListByBatchIdAndSendStatusApi = async (batchId, sendStatus) => {
    try {
      startLoading();
      const response = await getLogsListByBatchIdAndSendStatus(
        batchId,
        sendStatus,
      );
      if (response.status === 200) {
        if (
          !response?.data ||
          (Array.isArray(response.data) && response.data.length === 0)
        ) {
          setSelectedBatchIdData(['No data found']);
        } else {
          setSelectedBatchIdData(response.data);
        }
      }
      if (response.status === 204) {
        setSelectedBatchIdData(['No data found']);
        return;
      }
    } catch (error) {
      // handleApiError('error', error.response.data);
      setResponse(null);
      throw error;
    } finally {
      stopLoading();
    }
  };

  const resendEmailRecordsAPI = async (batchId, handleClose) => {
    try {
      startLoading();
      const response = await resendEmails(batchId);
      if (response.status === 200) {
        showAlert('success', 'Email resent successfully');
        handleClose();
      }
      await getLogsListAPI(); // Refresh the logs list
    } catch (error) {
      handleApiError(
        'error',
        error?.response?.data || 'Failed to resend email',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  };

  const abortMailApi = async (abortMailList, handleClose) => {
    try {
      startLoading();
      const response = await abortEmailRecords(abortMailList);
      if (response.status === 200) {
        showAlert('success', 'Email aborted successfully');
        handleClose(); // Close the modal after aborting
      }
      await getPendingMailRecordsAPI(); // Refresh the pending mail records
    } catch (error) {
      handleApiError(
        'error',
        error?.response?.data || 'Failed to abort email',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  };

  const loadEmailFileAPI = async (file) => {
    try {
      startLoading();
      const response = await loadEmailFile(file);
      if (response.status === 200) {
        showAlert('success', 'Email file loaded successfully');
      }
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const dowloadLogExcelFileAPI = async (batchId, sendStatus, handleClose) => {
    try {
      startLoading();
      const response = await dowloadLogExcelFile(batchId, sendStatus);
      if (response.status === 200) {
        // Create a link element to download the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `logs_batch_${batchId}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showAlert('success', 'Log file downloaded successfully');
        handleClose(); // Close the modal after download
      }
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const getPendingMailRecordsAPI = async () => {
    try {
      startLoading();
      const response = await getPendingMailRecords();
      if (response.status === 200) {
        setPendingMailRec(response.data);
      }
      if (response.status === 204) {
        setPendingMailRec(['No data found']);
        return;
      }
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  // Set emailSender.id and template.id in paymentReminder
  const setEmailSenderAndTemplate = (emailSenderId, templateId, closeModal) => {
    setPaymentReminder((prev) => ({
      ...prev,
      emailSender: { id: emailSenderId },
      template: { id: templateId },
    }));
    closeModal();
  };

  return (
    <PaymentReminderContext.Provider
      value={{
        uploadFile,
        response,
        setResponse,
        isLoading,
        startLoading,
        stopLoading,
        getPaymentReminderFilesAPI,
        totalElements,
        setTotalElements,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        setSortBy,
        setSortDir,
        sortBy,
        sortDir,
        unloadFile,
        getPaymentReminderFileByFileIdAPI,
        singleFileData,
        setSingleFileData,
        paymentReminderSenderEmailAPI,
        senderEmail,
        setSenderEmail,
        paymentReminderSenderEmailAPIId,
        emailTemplate,
        setEmailTemplate,
        updateEmailTemplateAPI,
        selectedFrom,
        setSelectedFrom,
        selectedReminder,
        setSelectedReminder,
        selectedSubject,
        setSelectedSubject,
        selectedBody,
        setSelectedBody,
        getLogsListAPI,
        logData,
        setLogData,
        getLogsListByBatchIdAndSendStatusApi,
        selectedBatchIdData,
        setSelectedBatchIdData,
        paymentReminder,
        setPaymentReminder,
        intialState,
        resetPaymentReminder,
        toggleFileLogSelection,
        updateFileLogEmailInPaymentReminder,
        setEmailSenderAndTemplate,
        sendEmailRecordsAPI,
        loadEmailFileAPI,
        resendEmailRecordsAPI,
        dowloadLogExcelFileAPI,
        getPendingMailRecordsAPI,
        pendingMailRec,
        abortMailList,
        setAbortMailList,
        abortMailApi,
      }}
    >
      {children}
    </PaymentReminderContext.Provider>
  );
};
export default PaymentReminderContextProvider;
